import { useEffect, useState } from 'react';
import { Smartphone, WifiOff, Printer, Cloud, ArrowRight, Check, ChevronDown, ChevronUp, User, Store, ShoppingBag, Rocket } from 'lucide-react';

const posUrl = import.meta.env.VITE_POS_URL || 'http://localhost:5173';
const posRegisterUrl = `${posUrl}/register`;

const features = [
  {
    icon: Smartphone,
    title: 'Tambah ke Home Screen',
    description: 'Tambahkan ke layar utama perangkat anda, seperti aplikasi native.',
    color: 'bg-sky-100 text-sky-600',
  },
  {
    icon: WifiOff,
    title: 'Tidak Perlu Internet',
    description: 'Bertransaksi tanpa internet. Data tersimpan aman di perangkat Anda.',
    color: 'bg-green-100 text-green-600',
  },
  {
    icon: Printer,
    title: 'Cetak Struk Otomatis',
    description: 'Kompatibel dengan printer Bluetooth ESC/POS. Cetak struk lebih cepat.',
    color: 'bg-orange-100 text-orange-600',
  },
  {
    icon: Cloud,
    title: 'Sinkronisasi Otomatis',
    description: 'Data tersimpan otomatis ke Google Sheets. Tidak perlu khawatir kehilangan data.',
    color: 'bg-purple-100 text-purple-600',
  },
  // {
  //   icon: Users,
  //   title: 'Multi-User',
  //   description: 'Bisa digunakan oleh beberapa kasir sekaligus dengan akun yang berbeda.',
  //   color: 'bg-pink-100 text-pink-600',
  // },
  // {
  //   icon: Package,
  //   title: 'Kelola Stok',
  //   description: 'Kelola inventaris produk dengan mudah. Notifikasi stok menipis otomatis.',
  //   color: 'bg-yellow-100 text-yellow-600',
  // },
  // {
  //   icon: BarChart3,
  //   title: 'Laporan Penjualan',
  //   description: 'Lihat laporan penjualan harian, mingguan, dan bulanan dengan detail.',
  //   color: 'bg-indigo-100 text-indigo-600',
  // },
  // {
  //   icon: QrCode,
  //   title: 'QRIS Payment',
  //   description: 'Terima pembayaran QRIS dari berbagai aplikasi dengan mudah.',
  //   color: 'bg-teal-100 text-teal-600',
  // },
];

const steps = [
  {
    icon: User,
    title: 'Daftar Akun',
    description: 'Buat akun gratis dalam hitungan detik, langsung bisa digunakan',
  },
  {
    icon: Store,
    title: 'Konfigurasi Toko',
    description: 'Masukkan nama toko, alamat, dan informasi lainnya dengan mudah',
  },
  {
    icon: ShoppingBag,
    title: 'Tambah Produk',
    description: 'Masukkan nama, harga, dan stok produk dengan mudah',
  },
  {
    icon: Rocket,
    title: 'Mulai Berjualan',
    description: 'Langsung bisa proses transaksi dan cetak struk',
  },
];

const pricingPlans = [
  {
    name: 'Basic',
    price: 'Gratis',
    features: [
      'Tambah ke Home Screen',
      'Tanpa internet',
      'Cetak Struk',
      'Sinkronisasi ke Google Sheets',
    ],
    popular: true,
  },
  {
    name: 'Pro',
    price: 'Coming Soon',
    features: [
      'coming soon',
      'coming soon',
      'coming soon',
      'coming soon',
    ],
    popular: false,
  },
];

const faqs = [
  {
    question: 'Apakah bisa digunakan tanpa internet?',
    answer: 'Setelah anda berhasil login, LancarPOS akan bekerja tanpa internet. Data tersimpan di perangkat anda.',
  },
  {
    question: 'Apakah data aman?',
    answer: 'Ya, data tersimpan di perangkat Anda dan bisa di-backup ke Google Sheets.',
  },
  {
    question: 'Berapa harga lisensi?',
    answer: 'Versi Basic gratis selamanya. Versi Pro dengan fitur tambahan akan segera hadir.',
  },
  {
    question: 'Apakah support printer thermal?',
    answer: 'Ya, support printer Bluetooth dengan protokol ESC/POS.',
  },
  {
    question: 'Bisakah ganti perangkat?',
    answer: 'Ya, cukup login di perangkat baru dan data akan tersinkronisasi dari Google Sheets.',
  },
];

function AnimatedSection({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, delay);
    return () => clearTimeout(timer);
  }, [delay]);

  return (
    <div
      className={`transition-all duration-700 ease-out ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
    >
      {children}
    </div>
  );
}

function Navbar() {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <nav className={`fixed top-0 left-0 right-0 bg-white/90 backdrop-blur-md border-b border-gray-100 z-50 transition-all duration-300 ${mounted ? 'opacity-100' : 'opacity-0'}`}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-14">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-sky-500 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">L</span>
            </div>
            <span className="text-lg font-bold text-gray-900">LancarPOS</span>
          </div>
          <div className="hidden sm:flex items-center gap-6">
            <a href="#fitur" className="text-sm font-medium text-gray-600 hover:text-sky-600 transition-colors">
              Fitur
            </a>
            <a href="#cara-pakai" className="text-sm font-medium text-gray-600 hover:text-sky-600 transition-colors">
              Cara Pakai
            </a>
            <a href="#harga" className="text-sm font-medium text-gray-600 hover:text-sky-600 transition-colors">
              Harga
            </a>
            <a
              href={posUrl}
              className="px-4 py-2 bg-sky-500 text-white text-sm font-medium rounded-lg hover:bg-sky-600 transition-colors"
            >
              Masuk
            </a>
          </div>
          <div className="flex sm:hidden items-center gap-2">
            <a
              href={posUrl}
              className="px-3 py-1.5 bg-sky-500 text-white text-sm font-medium rounded-lg hover:bg-sky-600 transition-colors"
            >
              Masuk
            </a>
          </div>
        </div>
      </div>
    </nav>
  );
}

function Hero() {
  return (
    <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-sky-50">
      <div className="max-w-3xl mx-auto text-center">
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
          Internet Mati? <span className="text-sky-500">Transaksi Tetap Jalan</span>
        </h1>
        <p className="text-lg sm:text-xl text-gray-600 mb-10 max-w-xl mx-auto">
          Kasir online yang tetap jalan tanpa internet. 
          Data tersimpan aman di perangkat anda.
        </p>
        <a
          href={posRegisterUrl}
          className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-sky-500 text-white rounded-xl font-semibold text-lg hover:bg-sky-600 transition-all hover:scale-105 shadow-lg shadow-sky-500/25"
        >
          Mulai Gratis
          <ArrowRight className="w-5 h-5" />
        </a>
        {/* <p className="mt-4 text-sm text-gray-500">
          1,000+ pelaku usaha sudah menggunakan
        </p> */}
      </div>
    </section>
  );
}

function Features() {
  return (
    <section id="fitur" className="py-16 sm:py-20 bg-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection>
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Fitur Unggulan
            </h2>
            <p className="text-gray-600 max-w-xl mx-auto">
              Dilengkapi dengan berbagai fitur yang memudahkan operasional toko Anda
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div
                key={index}
                className="group p-6 rounded-2xl bg-white border border-gray-200 hover:border-sky-200 hover:shadow-xl hover:shadow-sky-100/50 transition-all duration-300"
              >
                <div className={`w-12 h-12 ${feature.color} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                  <feature.icon className="w-6 h-6" />
                </div>
                <h3 className="text-base font-semibold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section id="cara-pakai" className="py-16 sm:py-20 bg-sky-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection delay={200}>
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Cara Menggunakan
            </h2>
            <p className="text-gray-600 max-w-xl mx-auto">
              Hanya 4 langkah mudah untuk mulai berjualan dengan LancarPOS
            </p>
          </div>
        </AnimatedSection>
        
        <div className="relative">
          <div className="hidden sm:block absolute top-1/2 left-0 right-0 h-0.5 bg-sky-200 -translate-y-1/2 z-0" />
          <div className="grid sm:grid-cols-4 gap-6 relative z-10">
            {steps.map((step, index) => (
              <AnimatedSection key={index} delay={300 + index * 100}>
                <div className="text-center">
                  <div className="w-16 h-16 bg-white border-2 border-sky-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <step.icon className="w-7 h-7 text-sky-600" />
                  </div>
                  <div className="inline-flex items-center justify-center w-7 h-7 bg-sky-500 text-white rounded-full text-sm font-bold mx-auto mb-3">
                    {index + 1}
                  </div>
                  <h3 className="text-base font-semibold text-gray-900 mb-1">
                    {step.title}
                  </h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{step.description}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Pricing() {
  return (
    <section id="harga" className="py-16 sm:py-20 bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection delay={400}>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-12">
            Harga
          </h2>
        </AnimatedSection>
        
        <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto">
          {pricingPlans.map((plan, index) => (
            <AnimatedSection key={plan.name} delay={500 + index * 100}>
              <div
                className={`p-6 rounded-xl border-2 ${
                  plan.popular
                    ? 'border-sky-500 bg-white'
                    : 'border-gray-200 bg-white opacity-75'
                }`}
              >
                {plan.popular && (
                  <div className="text-center mb-4">
                    <span className="inline-block px-3 py-1 bg-sky-500 text-white text-xs font-medium rounded-full">
                      Paling Populer
                    </span>
                  </div>
                )}
                
                <div className="text-center mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">{plan.name}</h3>
                  <div className="text-2xl font-bold text-gray-900">{plan.price}</div>
                </div>
                
                <ul className="space-y-2 mb-6">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                      <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                
                <a
                  href={plan.popular ? posRegisterUrl : '#'}
                  className={`block w-full py-3 rounded-xl font-medium text-center transition-all ${
                    !plan.popular
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-sky-500 text-white hover:bg-sky-600'
                  }`}
                >
                  {plan.popular ? 'Mulai Gratis' : plan.price}
                </a>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section className="py-16 sm:py-20">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection delay={600}>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-12">
            Pertanyaan Umum
          </h2>
        </AnimatedSection>
        
        <div className="space-y-3">
          {faqs.map((faq, index) => (
            <AnimatedSection key={index} delay={700 + index * 50}>
              <div
                className="border border-gray-200 rounded-lg overflow-hidden"
              >
                <button
                  onClick={() => setOpenIndex(openIndex === index ? null : index)}
                  className="w-full px-6 py-4 text-left flex justify-between items-center bg-white hover:bg-gray-50 transition-colors"
                >
                  <span className="font-medium text-gray-900 text-sm">
                    {faq.question}
                  </span>
                  {openIndex === index ? (
                    <ChevronUp className="w-5 h-5 text-gray-500 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
                  )}
                </button>
                {openIndex === index && (
                  <div className="px-6 py-4 bg-gray-50 text-gray-600 text-sm">
                    {faq.answer}
                  </div>
                )}
              </div>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTASection() {
  return (
    <section className="py-16 sm:py-20 bg-sky-500">
      <AnimatedSection delay={800}>
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
            Siap Memulai?
          </h2>
          <p className="text-sky-100 mb-8">
            Mulai gunakan LancarPOS gratis untuk bisnis Anda
          </p>
          <a
            href={posRegisterUrl}
            className="inline-flex items-center gap-2 px-8 py-4 bg-white text-sky-500 rounded-xl font-semibold text-lg hover:bg-gray-50 transition-all hover:scale-105 shadow-lg"
          >
            Mulai Gratis
            <ArrowRight className="w-5 h-5" />
          </a>
        </div>
      </AnimatedSection>
    </section>
  );
}

function Footer() {
  return (
    <footer className="py-8 bg-gray-900">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-sky-500 rounded flex items-center justify-center">
              <span className="text-white font-bold text-sm">L</span>
            </div>
            <span className="text-white font-semibold">LancarPOS</span>
          </div>
          <p className="text-gray-400 text-sm">
            Dibuat dengan ❤️ di Bantul, <a href="https://www.linkedin.com/in/andhikayuana" target="_blank" className="text-sky-400 hover:underline">Andhika Yuana</a>
          </p>
          <p className="text-gray-400 text-sm">© 2026 LancarPOS. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
}

function App() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main>
        <Hero />
        <Features />
        <HowItWorks />
        <Pricing />
        <FAQ />
        <CTASection />
      </main>
      <Footer />
    </div>
  );
}

export default App;
